// client/src/constants/skillLevels.js

/**
 * Centralized skill level constants for tennis ratings.
 *
 * These represent NTRP (National Tennis Rating Program) levels commonly used
 * in tennis league play.
 *
 * Parity (Phase 1D): The canonical persisted/domain skill level values live
 * server-side in `server/constants/skillLevels.js` (`VALID_SKILL_LEVELS`).
 * This module intentionally mirrors those persisted values in
 * `SKILL_LEVEL_VALUES` and is kept in sync by `scripts/check-constant-parity.js`.
 * Existing import paths are preserved; direct shared-path imports are
 * intentionally deferred (see docs/designs/react-native-code-sharing-audit.md).
 *
 * Only `SKILL_LEVEL_VALUES` is the parity-protected domain list. Display labels,
 * category groupings, and helper copy below are client-only presentation
 * concerns and are intentionally NOT forced into client/server parity.
 */

/**
 * Canonical persisted/domain skill level values (parity-protected mirror).
 *
 * Must stay in sync, in canonical order, with the backend source of truth:
 * server/constants/skillLevels.js (`VALID_SKILL_LEVELS`).
 */
export const SKILL_LEVEL_VALUES = Object.freeze([
  2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5, 6.0,
])

/**
 * Available skill levels for player selection.
 *
 * Derived from the parity-protected `SKILL_LEVEL_VALUES`. The `label` field is
 * a client-only display string and is intentionally excluded from parity.
 */
export const SKILL_LEVELS = Object.freeze(
  SKILL_LEVEL_VALUES.map((value) => ({ value, label: value.toFixed(1) })),
)

/**
 * Simple category labels for skill levels
 * Can be used for future grouping or display purposes
 *
 * Client-only display grouping. Intentionally NOT parity-protected.
 */
export const SKILL_LEVEL_CATEGORIES = Object.freeze({
  BEGINNER: [2.5],
  INTERMEDIATE: [3.0, 3.5, 4.0, 4.5],
  ADVANCED: [5.0, 5.5, 6.0],
})

/**
 * Get category label for a given skill level
 * @param {number} level - Skill level value
 * @returns {string} Category label or empty string if not found
 */
export function getSkillLevelCategory(level) {
  if (SKILL_LEVEL_CATEGORIES.BEGINNER.includes(level)) return 'Beginner'
  if (SKILL_LEVEL_CATEGORIES.INTERMEDIATE.includes(level)) return 'Intermediate'
  if (SKILL_LEVEL_CATEGORIES.ADVANCED.includes(level)) return 'Advanced'
  return ''
}
